import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { deleteAccount, deleteQuestionInfo, getAllUnitsAndObjectives, getAssignmentListByTeacher, importQuestionBank, setSchoolPress, clearSurveyBySchool } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { getSubjectDisplayName } from '@/hooks/use-subject-name';
import { AssignmentModel } from '@/services/generatedAPIs.schemas';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import XLSX from 'xlsx';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

import { useAsyncAction } from '@/hooks/use-async-action';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ReadonlyQuestionDisplay } from '@/components/readonly-question-display';
import { Badge } from '@/components/ui/badge';
import { convertSpacesToLatex, processBackslashes } from '@/utils/preprocess';

type QuestionBankData = {
    Aisolve: string
    answer: string
    answer_translated: string
    diff_0: string
    difficulty: string
    grade: string
    id: string
    language: string
    obj_id: string | string[]
    objective: string | string[]
    press: string
    question: string
    question_translated: string
    type: string
    unit: string | string[]
    unit_id: string | string[]
    version: string
    scope: string
    state: string
    press_marker: string
}

type ProblematicRow = QuestionBankData & { _errors: string[] }

type TopicInfo = { id?: number; type?: string; mtrId?: string }

// --- Validation functions ---

function validateTopicMatching(row: QuestionBankData, topicInfos: TopicInfo[]): string | null {
    const hasMatchingTopic = topicInfos.some(
        (info) => info.type === 'topic' && (row.unit_id as string[]).includes(info.mtrId ?? '')
    );
    const hasMatchingKP = topicInfos.some(
        (info) => info.type === 'knowledge_point' && (row.obj_id as string[]).includes(info.mtrId ?? '')
    );

    const errors: string[] = [];
    if (!hasMatchingTopic) errors.push(`unit_id [${(row.unit_id as string[]).join(', ')}] not found`);
    if (!hasMatchingKP) errors.push(`obj_id [${(row.obj_id as string[]).join(', ')}] not found`);

    return errors.length > 0 ? errors.join('; ') : null;
}

function validateLatexContent(row: QuestionBankData): string | null {
    const fieldsToCheck: { name: string; value: string | undefined }[] = [
        { name: 'question', value: row.question },
        { name: 'question_translated', value: row.question_translated },
        { name: 'answer', value: row.answer },
        { name: 'answer_translated', value: row.answer_translated },
    ];

    const errors: string[] = [];
    // Flag LaTeX special characters that need escaping: % & _ ^ ~ # { }
    const unescapedPattern = /(?<!\\)[%&_~]/g;

    for (const field of fieldsToCheck) {
        if (!field.value) continue;
        // Remove \text{...} patterns from validation check (characters inside \text{} are valid)
        let textToCheck = field.value.replace(/\\text\{[^}]*\}/g, '');
        // Remove {multiple alphanumerics}_{multiple alphanumerics} patterns (LaTeX subscript notation)
        textToCheck = textToCheck.replace(/[a-zA-Z0-9]+_[a-zA-Z0-9({]+/g, '');
        const specialMatches = textToCheck.match(unescapedPattern);
        if (specialMatches) {
            const uniqueChars = [...new Set(specialMatches)].join(' ');
            errors.push(`${field.name}: unescaped ${uniqueChars}`);
        }
        // Flag $ that aren't part of valid math delimiter pairs.
        // Valid pairs: $$...$$ (display math) or $...$ (inline math)
        // Closing $ must be followed by space, punctuation, or end of string.
        // Strip valid pairs first ($$...$$ before $...$), then check for remaining $.
        // Punctuation includes English and Traditional Chinese: ，。、；：！？「」『』（）【】《》〈〉…—～·
        // Content pattern (?:[^$\\]|\\.)* handles escaped chars inside math blocks:
        //   - $\$4,500$ — \$ is consumed by \\. so inner $ isn't treated as a delimiter
        //   - $672 \div 21 = 32$ — \d consumed by \\., rest by [^$\\]
        //   - '$3$' or "$3$" — quotes added to validClosing so closing $ before ' or " is accepted
        const validClosing = /(?=[\s,.:;!?\)\]\}'"，。、；：！？「」『』（）【】《》〈〉…—～·]|$)/;
        const strippedDollar = field.value
            .replace(new RegExp(`(?<!\\\\)\\$\\$(?:[^$\\\\]|\\\\.)*\\$\\$${validClosing.source}`, 'g'), '')
            .replace(new RegExp(`(?<!\\\\)\\$(?:[^$\\\\]|\\\\.)*\\$${validClosing.source}`, 'g'), '');
        const remainingDollars = (strippedDollar.match(/(?<!\\)\$/g) || []).length;
        if (remainingDollars > 0) {
            errors.push(`${field.name}: invalid $ usage (${remainingDollars} unpaired/misused)`);
        }
        // Check for \angle followed by non-space character
        const anglePattern = /\\angle(?!\s)/g;
        const angleMatches = [...field.value.matchAll(anglePattern)];
        if (angleMatches.length > 0) {
            errors.push(`${field.name}: \\angle must be followed by space (${angleMatches.length} occurrences)`);
        }
    }

    return errors.length > 0 ? `LaTeX issues: ${errors.join('; ')}` : null;
}

const MCQ_OPTION_PATTERNS = [
    /(?:^|\n\s*)([A-H])\)\s*([^]*?)(?=\n[A-H]\)|$)/g,
    /(?:^|\n\s*)\(([A-H])\)\s*([^(]*?)(?=\n\([A-H]\)|$)/g,
    /(?:^|\n\s*)([A-H])：\s*([^]*?)(?=\n[A-H]：|$)/g,
    /(?:^|\n\s*)([A-H]):\s*([^]*?)(?=\n[A-H]:|$)/g,
    /(?:^|\n\s*)([A-H])\.\s*([^]*?)(?=\n\s*[A-H]+\.|$)/g,
];

/**
 * Fix MCQ options format by adding newlines before each option.
 * Transforms horizontal options like "A. 54\t\tB. 98" into vertical format.
 */
function fixMCQOptionsFormat(text: string): string {
    if (!text) return text;

    console.log('Before fix:', text);

    // Pattern to match option markers (A), B), (A), A., A:, A：) that are not at the start of a line
    // and add a newline before them
    const fixed = text
        // Fix A) B) C) D) format (without parentheses around letter)
        .replace(/([^\n])\s+([A-H])\)/g, '$1\n$2)')
        // Fix (A) (B) (C) (D) format
        .replace(/([^\n])\s+\(([A-H])\)/g, '$1\n($2)')
        // Fix A. B. C. D. format (but not if it's already at start of line or after newline)
        .replace(/([^\n])\s+([A-H])\./g, '$1\n$2.')
        // Fix A: B: C: D: format
        .replace(/([^\n])\s+([A-H]):/g, '$1\n$2:')
        // Fix A： B： C： D： format (Chinese colon)
        .replace(/([^\n])\s+([A-H])：/g, '$1\n$2：');

    console.log('AAfter fix:', fixed);

    return fixed;
}

function validateMCQFormat(row: QuestionBankData): string | null {
    if (row.type !== 'MCQ') return null;

    const fieldsToCheck: { name: string; value: string | undefined }[] = [
        { name: 'question', value: row.question },
        { name: 'question_translated', value: row.question_translated },
    ];

    const invalidFields: string[] = [];

    for (const field of fieldsToCheck) {
        if (!field.value) continue;
        let hasValidOptions = false;
        for (const pattern of MCQ_OPTION_PATTERNS) {
            pattern.lastIndex = 0;
            const matches = [...field.value.matchAll(pattern)];
            if (matches.length >= 4) {
                hasValidOptions = true;
                break;
            }
        }
        if (!hasValidOptions) {
            invalidFields.push(field.name);
        }
    }

    if (invalidFields.length > 0) {
        return `MCQ options not found in: ${invalidFields.join(', ')} (expected A) / B), (A) / (B), A：/B：, or A. /B. with newlines)`;
    }

    return null;
}

function validateDifficulty(row: QuestionBankData): string | null {
    const validDifficulties = ['Easy', 'Intermediate', 'Hard'];
    if (row.difficulty && !validDifficulties.includes(row.difficulty)) {
        return `invalid difficulty "${row.difficulty}" (must be Easy, Intermediate, or Hard)`;
    }
    return null;
}

function validateGrade(row: QuestionBankData): string | null {
    const validGrades = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', '1', '2', '3', '4', '5', '6'];
    if (row.grade && !validGrades.includes(row.grade)) {
        return `invalid grade "${row.grade}" (must be P1-P6 or 1-6)`;
    }
    return null;
}

function validateType(row: QuestionBankData): string | null {
    const validTypes = ['FRQ', 'MCQ'];
    if (row.type && !validTypes.includes(row.type)) {
        return `invalid type "${row.type}" (must be FRQ or MCQ)`;
    }
    return null;
}

function validateLanguage(row: QuestionBankData): string | null {
    const validLanguages = ['c', 'e', 'b'];
    if (row.language && !validLanguages.includes(row.language)) {
        return `invalid language "${row.language}" (must be c, e, or b)`;
    }
    return null;
}

function validateState(row: QuestionBankData): string | null {
    const validStates = ['0', '1'];
    if (row.state && !validStates.includes(row.state)) {
        return `invalid state "${row.state}" (must be 0 or 1)`;
    }
    return null;
}

function validateScope(row: QuestionBankData): string | null {
    const validScopes = ['1', '2', '3'];
    if (row.scope && !validScopes.includes(row.scope)) {
        return `invalid scope "${row.scope}" (must be 1, 2, or 3)`;
    }
    return null;
}

function validatePressMarker(row: QuestionBankData): string | null {
    const validMarkers = ['work', 'exam'];
    if (row.press_marker && !validMarkers.includes(row.press_marker)) {
        return `invalid press_marker "${row.press_marker}" (must be work, exam, or empty)`;
    }
    return null;
}

function validateImagePlaceholders(row: QuestionBankData, imageFiles: File[]): string | null {
    const fieldsToCheck: { name: string; value: string | undefined }[] = [
        { name: 'question', value: row.question },
        { name: 'question_translated', value: row.question_translated },
        { name: 'answer', value: row.answer },
        { name: 'answer_translated', value: row.answer_translated },
    ];

    const errors: string[] = [];
    const imagePattern = /\[@\]([^@\]]+)\[@\]/g;
    const bareAtPattern = /(?<!\[)@([^@]+)@(?!\])/g;

    for (const field of fieldsToCheck) {
        if (!field.value) continue;

        // Check for bare @...@ format (wrong delimiter — should be [@]...[@])
        bareAtPattern.lastIndex = 0;
        for (const bareMatch of field.value.matchAll(bareAtPattern)) {
            errors.push(`${field.name}: invalid image placeholder format "@${bareMatch[1]}@" (use [@]${bareMatch[1]}[@] instead)`);
        }

        // Reset regex lastIndex for each field
        imagePattern.lastIndex = 0;
        const matches = [...field.value.matchAll(imagePattern)];

        for (const match of matches) {
            const filename = match[1];
            // Check if filename has valid image extension
            const validExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg'];
            const hasValidExtension = validExtensions.some(ext => filename.toLowerCase().endsWith(ext));

            if (!hasValidExtension) {
                errors.push(`${field.name}: invalid image format "${filename}" (must end with .png, .jpg, .jpeg, .gif, or .svg)`);
            }

            // Check for basic filename format (no invalid characters)
            // if (!/^[a-zA-Z0-9_.-]+$/.test(filename)) {
            //     errors.push(`${field.name}: invalid filename "${filename}" (only alphanumeric, underscore, hyphen, and dot allowed)`);
            // }

            // Check if the referenced image file exists in the uploaded files
            const fileExists = imageFiles.some(file => file.name === filename);
            if (!fileExists) {
                errors.push(`${field.name}: referenced image "${filename}" not found in uploaded files`);
            }
        }
    }

    return errors.length > 0 ? errors.join('; ') : null;
}

// --- Helper functions ---

function difficultyToNumber(difficulty: string): number {
    if (difficulty === "Easy") {
        return 1;
    } else if (difficulty === "Intermediate") {
        return 2;
    } else if (difficulty === "Hard") {
        return 3;
    } else {
        return 2;
    }
}

function parseSchoolName(text: string): string {
    switch (text) {
        case '堅樂中學': return 'The Mission Covenant Church Holm Glad College';
        case 'WingLung': return 'Baptist Wing Lung Secondary School';
        case '18': return 'SPHRC Kung Yik She Secondary School';
        case '曾璧山中學': return 'Tsang Pik Shan (Sung Lan) Secondary School';
        case '蒙黃花沃紀念小學': return 'C.C.C. Mong Wong Far Yok Memorial Primary School';
        case 'Press': return 'SUP';
        case 'United Prime': return 'United Prime';
        default: return text;
    }
}

function parseSchoolType(text: string): string {
    switch (text) {
        case 'CE/DSE': return 'CE/DSE';
        case 'TSA': return 'TSA';
        case 'ByteDance':
        case 'DeepSeek':
        case 'Gemini':
        case 'Poe':
        case 'Poe Assistant': return 'AI';
        case 'Press': return 'Press';
        case 'EPH': return 'Press';
        case 'United Prime': return 'Press';
        default: return 'school';
    }
}

function latexCodify(text?: string): string | undefined {
    if (typeof text === 'undefined') {
        return;
    }

    if (typeof text !== 'string') {
        text = text.toString();
    }
    return text;
}

const LibraryImportPage = () => {
    const { t, i18n } = useTranslation();
    const { getCurrentUser } = useAuth();
    const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
    const { subjectInfos } = useSubjectList();
    const subjectName = getSubjectDisplayName(subjectInfos, subjectInfos[0]?.id);
    const [statusMessage, setStatusMessage] = useState('');
    const [excelFileName, setExcelFileName] = useState('');
    const [importSummary, setImportSummary] = useState<{
        totalQuestions: number;
        validCount: number;
        problematicCount: number;
        totalImages: number;
        skipped: number;
        success?: boolean;
    } | null>(null);
    const [problematicRows, setProblematicRows] = useState<ProblematicRow[]>([]);
    const [previewRow, setPreviewRow] = useState<ProblematicRow | null>(null);
    const [allOriginalRows, setAllOriginalRows] = useState<Record<string, unknown>[]>([]);
    const [originalColumnOrder, setOriginalColumnOrder] = useState<string[]>([]);

    // Store validated data for the confirm step
    const validatedDataRef = useRef<{
        data: any[];
        imageFiles: File[];
        archivedQuestionIds: string[];
    } | null>(null);

    const handleFolderSelect = (files: any) => execute(async () => {
        if (!files || files.length === 0) {
            return;
        }

        setImportSummary(null);
        setProblematicRows([]);
        validatedDataRef.current = null;
        setAllOriginalRows([]);
        setOriginalColumnOrder([]);
        setStatusMessage('Scanning files...');

        const imageFiles: File[] = [];
        let excelFile: File | null = null;
        for (const file of files) {
            if (file.type.startsWith('image/')) {
                imageFiles.push(new File([file], file.webkitRelativePath.substring(file.webkitRelativePath.indexOf('/') + 1), { type: file.type }));
            } else if (!file.name.startsWith('~') && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
                if (!excelFile) {
                    excelFile = file;
                }
            }
        }

        if (!excelFile) {
            setStatusMessage('No Excel file found in the selected folder.');
            return;
        }

        setExcelFileName(excelFile.name);

        // Read the Excel file
        await new Promise<void>((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (e) => {
              try {
                setStatusMessage('Parsing Excel file...');
                // 1. 获取二进制数据
                const binaryData = new Uint8Array(e.target.result);

                // 2. 解析Excel文件
                const workbook = XLSX.read(binaryData, { type: 'array' });

                // 3. 获取第一个工作表
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];

                // 4. Get column headers from the first row to preserve order
                const headerRow = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 0 })[0] as string[];
                setOriginalColumnOrder(headerRow);

                // 5. 转换为JSON数据
                const allRows: QuestionBankData[] = XLSX.utils.sheet_to_json(worksheet, { raw: false });
                setAllOriginalRows(JSON.parse(JSON.stringify(allRows)));
                let jsonData = allRows.filter((data) => data.unit_id && data.obj_id).filter((data) => data.press !== "TSA"); // Filter out rows missing unit_id, obj_id, or id
                const skippedRows = allRows.length - jsonData.length;

                if (jsonData && jsonData.length > 0) {
                    console.log('jsonData', jsonData[0])
                }

                const processListItem = (text: string): string[] => {
                    text = text.replaceAll(' ', '');
                    if (text.indexOf(',') !== -1) {
                        return text.split(',');
                    } else {
                        return [text];
                    }
                };

                for (let data of jsonData) {
                    data.obj_id = processListItem(data.obj_id as string);
                    data.objective = processListItem(data.objective as string);
                    data.unit_id = processListItem(data.unit_id as string);
                    data.unit = processListItem(data.unit as string);
                }

                setStatusMessage('Fetching units and objectives...');
                const labelRsp = await getAllUnitsAndObjectives();
                if (labelRsp.code === 200 && labelRsp.data) {
                    setStatusMessage('Validating data...');
                    const topicInfos = labelRsp.data.topicInfos;

                    // Collect archived question IDs and validate each row
                    const valid: QuestionBankData[] = [];
                    const problematic: ProblematicRow[] = [];
                    const archivedQuestionIds: string[] = [];

                    for (const row of jsonData) {
                        if (!row.id) continue; // skip rows without ID
                        if (row.state === '1') {
                            archivedQuestionIds.push(row.id);
                            continue; // skip archived questions
                        }

                        const errors: string[] = [];

                        const topicError = validateTopicMatching(row, topicInfos);
                        if (topicError) errors.push(topicError);

                        const latexError = validateLatexContent(row);
                        if (latexError) errors.push(latexError);

                        const mcqError = validateMCQFormat(row);
                        if (mcqError) errors.push(mcqError);

                        const difficultyError = validateDifficulty(row);
                        if (difficultyError) errors.push(difficultyError);

                        const gradeError = validateGrade(row);
                        if (gradeError) errors.push(gradeError);

                        const typeError = validateType(row);
                        if (typeError) errors.push(typeError);

                        const languageError = validateLanguage(row);
                        if (languageError) errors.push(languageError);

                        const stateError = validateState(row);
                        if (stateError) errors.push(stateError);

                        const scopeError = validateScope(row);
                        if (scopeError) errors.push(scopeError);

                        const pressMarkerError = validatePressMarker(row);
                        if (pressMarkerError) errors.push(pressMarkerError);

                        const imageError = validateImagePlaceholders(row, imageFiles);
                        if (imageError) errors.push(imageError);

                        if (errors.length > 0) {
                            problematic.push({ ...row, _errors: errors });
                        } else {
                            valid.push(row);
                        }
                    }

                    // Transform valid rows for import
                    const transformedData = valid.map((value) => ({
                        questionInfoId: parseInt(value.id),
                        questionInfo: {
                            question: latexCodify(value.question),
                            questionEn: latexCodify(value.question_translated),
                            answer: latexCodify(value.answer),
                            answerEn: latexCodify(value.answer_translated),
                            grade: value.grade.startsWith('P') ? Number.parseInt(value.grade.slice(1)) : Number.parseInt(value.grade) + 6,
                            questionType: value.type,
                            originName: parseSchoolName(value.press),
                            originType: parseSchoolType(value.press),
                            locale: value.language,
                            difficulty: difficultyToNumber(value.difficulty),
                            aiSolve: value.Aisolve === "y" ? true : false,
                            topicIds: topicInfos.filter((questionLabel) => {
                                if (questionLabel.type === "topic") {
                                    for (const unit_id of value.unit_id) {
                                        if (questionLabel.mtrId === unit_id) {
                                            return true;
                                        }
                                    }
                                }
                                return false;
                            }).map((questionLabel) => questionLabel.id!),
                            subject: subjectName,
                            knowledgePointIds: topicInfos.filter((questionLabel) => {
                                if (questionLabel.type === "knowledge_point") {
                                    for (const obj_id of value.obj_id) {
                                        if (questionLabel.mtrId === obj_id) {
                                            return true;
                                        }
                                    }
                                }
                                return false;
                            }).map((questionLabel) => questionLabel.id!),
                            version: parseInt(value.version ?? "0"),
                            scope: parseInt(value.scope ?? "3"),
                            pressMarker: value.press_marker,
                            archived: value.state === "1",
                        }
                    }));

                    // Store for confirm step
                    validatedDataRef.current = { data: transformedData, imageFiles, archivedQuestionIds };
                    setProblematicRows(problematic);
                    setImportSummary({
                        totalQuestions: jsonData.filter(r => !!r.id).length,
                        validCount: valid.length,
                        problematicCount: problematic.length,
                        totalImages: imageFiles.length,
                        skipped: skippedRows,
                    });
                    setStatusMessage('Validation complete. Review results and click Import.');
                } else {
                    setStatusMessage('Failed to fetch units and objectives' + (labelRsp.message ? ': ' + labelRsp.message : ''));
                    toast.error('Failed to fetch units and objectives');
                }

            } catch (error: any) {
                console.error('解析错误:', error.message, error.name, error.cause);
                setStatusMessage('Validation failed: ' + error.message);
                toast.error(t("component.fileerror"));
            }
            resolve();
          };

          reader.onerror = (e) => {
            console.log('reader error', e);
            setStatusMessage('Failed to read file.');
            toast.error(t('component.fileerror'));
            reject(e);
          };

          reader.readAsArrayBuffer(excelFile);
        });
    });

    const handleConfirmImport = () => execute(async () => {
        if (!subjectName) {
            toast.error(t('common.loading') || 'Subject data is loading, please wait.');
            return;
        }
        if (!validatedDataRef.current) return;

        const { data, imageFiles, archivedQuestionIds } = validatedDataRef.current;
        setStatusMessage(`Uploading ${data.length} questions and ${imageFiles.length} images...`);

        try {
            let rsp = await importQuestionBank({ images: imageFiles, model: JSON.stringify({ 'editQuestionInfoRequestList': data }) }, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (rsp.code === 200 && rsp.data) {
                toast.success('数据导入成功');
            } else {
                toast.error('数据导入失败 ' + rsp.message);
                setStatusMessage('Import failed: ' + rsp.message);
                setImportSummary(prev => prev ? { ...prev, success: false } : null);
                return;
            }

            setStatusMessage('Configuring school press settings...');

            await setSchoolPress(
                { pressList: ["SUP", "United Prime"] },
                { schoolName: "Baptist Wing Lung Secondary School" },
            );

            await setSchoolPress(
                { pressList: ["EPH"] },
                { schoolName: "Fanling Kau Yan College" },
            );

            await setSchoolPress(
                { pressList: ["EPH"] },
                { schoolName: "The Mission Covenant Church Holm Glad College" },
            );

            await setSchoolPress(
                { pressList: ["EPH"] },
                { schoolName: "C.C.C. Mong Wong Far Yok Memorial Primary School" },
            );

            rsp = await setSchoolPress(
                { pressList: ["EPH"] },
                { schoolName: "Chung Sing Benevolent Society Mrs Aw Boon Haw Secondary School" },
            );

            if (rsp.code === 200 && rsp.data) {
                toast.success('出版社设置成功');
            } else {
                toast.error('出版社设置失败 ' + rsp.message);
            }

            // Delete archived questions
            if (archivedQuestionIds.length > 0) {
                setStatusMessage(`Deleting ${archivedQuestionIds.length} archived questions...`);
                for (const id of archivedQuestionIds) {
                    await deleteQuestionInfo({ questionInfoId: parseInt(id) });
                }
                toast.success(`Deleted ${archivedQuestionIds.length} archived questions`);
            }

            setStatusMessage('Import complete');
            setImportSummary(prev => prev ? { ...prev, success: true } : null);
            validatedDataRef.current = null;
        } catch (error: any) {
            console.error('Import error:', error.message);
            setStatusMessage('Import failed: ' + error.message);
            setImportSummary(prev => prev ? { ...prev, success: false } : null);
            toast.error('Import failed: ' + error.message);
        }
    });

    const handleExportProblematic = () => {
        if (allOriginalRows.length === 0) return;

        // Create a map of row IDs to their error messages
        const errorMap = new Map<string, string>();
        for (const row of problematicRows) {
            errorMap.set(row.id, row._errors.join('; '));
        }

        // Export ALL original rows with an errors column
        const exportData = allOriginalRows.map(row => ({
            ...row,
            errors: (row.id && errorMap.has(String(row.id))) ? errorMap.get(String(row.id))! : '',
        }));
        // const exportData = allOriginalRows.map(row => {
        //     const hasError = row.id && errorMap.has(String(row.id));
        //     return {
        //         ...row,
        //         // Fix MCQ options format for rows with errors
        //         question_translated: hasError ? fixMCQOptionsFormat(row.question_translated ?? '') : row.question_translated,
        //         errors: hasError ? errorMap.get(String(row.id))! : '',
        //     };
        // });

        // Use original column order with 'errors' added at the end
        const headerOrder = [...originalColumnOrder, 'errors'];
        const ws = XLSX.utils.json_to_sheet(exportData, { header: headerOrder });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'All Rows with Errors');
        XLSX.writeFile(wb, 'all_rows_with_errors.xlsx');
    };

    const [assignmentList, setAssignmentList] = useState<AssignmentModel[]>();
    const [assignmentId, setAssignmentId] = useState<string>();
    const [assignmentName, setAssignmentName] = useState<string>();
    const [clearSurveySchoolName, setClearSurveySchoolName] = useState<string>('');

    useEffect(() => {
        getAssignmentListByTeacher({ teacherId: getCurrentUser()!.id }).then((rsp) => {
            if (rsp.code === 200 && rsp.data) {
                setAssignmentList(rsp.data.assignmentList);
            }
        }).catch((e) => {
            console.error("Error fetching assignment list", e);
        });
    }, []);

    return (
        <ContentLayout title={t("teacher.library")}>
            <LoadingOverlay isLoading={isLoading} text={t('common.loading')} />
            <div className='p-4'>
                <Input id="file" type="file" webkitdirectory="webkitdirectory" directory="directory" multiple disabled={isLoading} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFolderSelect(e.target.files)} />
                {excelFileName && (
                    <p className="text-sm text-muted-foreground mt-2">Excel: {excelFileName}</p>
                )}
            </div>

            {statusMessage && (
                <div className="px-4 pb-2">
                    <p className={`text-sm font-medium ${importSummary?.success === true ? 'text-green-600' : importSummary?.success === false ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {statusMessage}
                    </p>
                </div>
            )}

            {importSummary && (
                <div className="px-4 pb-4 space-y-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Import Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-1">
                            <p className="text-green-600">Valid rows: {importSummary.validCount}</p>
                            {importSummary.problematicCount > 0 && (
                                <p className="text-red-600">Problematic rows: {importSummary.problematicCount}</p>
                            )}
                            <p>Images: {importSummary.totalImages}</p>
                            {importSummary.skipped > 0 && (
                                <p className="text-muted-foreground">Skipped rows (missing unit/objective): {importSummary.skipped}</p>
                            )}
                        </CardContent>
                    </Card>

                    <div className="flex gap-2">
                        {validatedDataRef.current && validatedDataRef.current.data.length > 0 && importSummary.success === undefined && (
                            <Button onClick={handleConfirmImport} loading={isLoading}>
                                Confirm Import ({importSummary.validCount} rows)
                            </Button>
                        )}
                        {problematicRows.length > 0 && (
                            <Button variant="outline" onClick={handleExportProblematic}>
                                Download Problematic Rows ({importSummary.problematicCount})
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {problematicRows.length > 0 && (
                <div className="px-4 pb-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base text-red-600">Problematic Rows</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-80 overflow-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-16">ID</TableHead>
                                            <TableHead className="w-48">Question</TableHead>
                                            <TableHead>Errors</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {problematicRows.map((row, idx) => (
                                            <TableRow key={idx} className="cursor-pointer hover:bg-muted/50" onClick={() => setPreviewRow(row)}>
                                                <TableCell className="font-mono">{row.id}</TableCell>
                                                <TableCell className="max-w-48 truncate" title={row.question}>
                                                    {row.question?.slice(0, 60)}{row.question?.length > 60 ? '...' : ''}
                                                </TableCell>
                                                <TableCell className="text-red-600 text-xs">
                                                    {row._errors.map((err, i) => (
                                                        <div key={i}>{err}</div>
                                                    ))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Dialog open={!!previewRow} onOpenChange={(open) => { if (!open) setPreviewRow(null); }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>Question Preview (ID: {previewRow?.id})</DialogTitle>
                    </DialogHeader>
                    {previewRow && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                <Badge variant="outline">Type: {previewRow.type}</Badge>
                                <Badge variant="outline">Grade: {previewRow.grade}</Badge>
                                <Badge variant="outline">Difficulty: {previewRow.difficulty}</Badge>
                                <Badge variant="outline">Language: {previewRow.language}</Badge>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold mb-2">Errors</h4>
                                <div className="text-sm text-red-600 space-y-1">
                                    {previewRow._errors.map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold mb-2">Question (Rendered)</h4>
                                <div className="border rounded p-3 bg-muted/30">
                                    <ReadonlyQuestionDisplay questionText={previewRow.question || ''} />
                                </div>
                                <h4 className="text-sm font-semibold mb-2 mt-2">Question (Raw)</h4>
                                <pre className="border rounded p-3 bg-muted/30 text-xs whitespace-pre-wrap break-all">{previewRow.question || ''}</pre>
                            </div>

                            {previewRow.type === 'MCQ' && (() => {
                                const text = previewRow.question || '';
                                for (const pattern of MCQ_OPTION_PATTERNS) {
                                    pattern.lastIndex = 0;
                                    const matches = [...text.matchAll(pattern)];
                                    if (matches.length >= 2) {
                                        return (
                                            <div>
                                                <h4 className="text-sm font-semibold mb-2">Parsed MCQ Options</h4>
                                                <div className="space-y-2">
                                                    {matches.map((match, i) => (
                                                        <div key={i} className="flex items-start gap-2 border rounded p-2 bg-muted/30">
                                                            <span className="font-semibold text-sm">{match[1]}.</span>
                                                            <div className="flex-1">
                                                                <ReadonlyQuestionDisplay questionText={convertSpacesToLatex(processBackslashes(match[2].trim()))} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                }
                                return (
                                    <div>
                                        <h4 className="text-sm font-semibold mb-2">MCQ Options</h4>
                                        <p className="text-sm text-red-600">Could not parse options from question text. Supported formats: A)/B), (A)/(B), A：/B：, A./B.</p>
                                    </div>
                                );
                            })()}

                            {previewRow.question_translated && (
                                <div>
                                    <h4 className="text-sm font-semibold mb-2">Question Translated (Rendered)</h4>
                                    <div className="border rounded p-3 bg-muted/30">
                                        <ReadonlyQuestionDisplay questionText={previewRow.question_translated} />
                                    </div>
                                    <h4 className="text-sm font-semibold mb-2 mt-2">Question Translated (Raw)</h4>
                                    <pre className="border rounded p-3 bg-muted/30 text-xs whitespace-pre-wrap break-all">{previewRow.question_translated}</pre>
                                </div>
                            )}

                            <div>
                                <h4 className="text-sm font-semibold mb-2">Answer (Rendered)</h4>
                                <div className="border rounded p-3 bg-muted/30">
                                    <ReadonlyQuestionDisplay questionText={previewRow.answer || ''} />
                                </div>
                                <h4 className="text-sm font-semibold mb-2 mt-2">Answer (Raw)</h4>
                                <pre className="border rounded p-3 bg-muted/30 text-xs whitespace-pre-wrap break-all">{previewRow.answer || ''}</pre>
                            </div>

                            {previewRow.answer_translated && (
                                <div>
                                    <h4 className="text-sm font-semibold mb-2">Answer Translated (Rendered)</h4>
                                    <div className="border rounded p-3 bg-muted/30">
                                        <ReadonlyQuestionDisplay questionText={previewRow.answer_translated} />
                                    </div>
                                    <h4 className="text-sm font-semibold mb-2 mt-2">Answer Translated (Raw)</h4>
                                    <pre className="border rounded p-3 bg-muted/30 text-xs whitespace-pre-wrap break-all">{previewRow.answer_translated}</pre>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Input name="name" className="w-1/2 m-4" onChange={(e) => { setAssignmentName(e.target.value); }} />
            <Button className="m-4" onClick={() => {
                if (assignmentName) {
                    deleteAccount({ email: assignmentName });
                }
            }}>assignment</Button>

            <div className="flex items-center gap-2 m-4">
                <Input
                    className="w-1/2"
                    placeholder="School name"
                    value={clearSurveySchoolName}
                    onChange={(e) => setClearSurveySchoolName(e.target.value)}
                />
                <Button
                    variant="destructive"
                    onClick={async () => {
                        if (!clearSurveySchoolName) return;
                        const rsp = await clearSurveyBySchool({ schoolName: clearSurveySchoolName });
                        if (rsp.code === 200) {
                            toast.success(`Cleared surveys for ${clearSurveySchoolName}`);
                        } else {
                            toast.error(`Failed: ${rsp.message}`);
                        }
                    }}
                >
                    Clear Survey by School
                </Button>
            </div>
        </ContentLayout>
    );
};

export default LibraryImportPage;
